// Render fakturoveho UI nad existujicim DOM (index.html).
// Strategie: vstupy (inputy/textareas/select) ridi stav pres event listenery,
// render() pouze synchronizuje hodnoty zpet do DOM (kdyz se zmeni ze stavu napr.
// po nacteni z localStorage nebo po posunuti splatnosti). Pro tabulku polozek
// rebuildujeme tbody jen kdyz se zmeni mnozina id polozek.

import { lineTotals, invoiceTotals } from './calc.js';
import { formatMoney, parseNumber } from './format.js';

const VAT_RATES = [21, 12, 0];
const UNITS = ['ks', 'kg', 'hod', 'měs', 'rok', ''];

let cachedItemIds = '';

/**
 * Provede plnou synchronizaci DOM podle stavu.
 * @param {ReturnType<typeof import('./state.js').getState>} state
 * @param {{ savedAt?: number|null }} ui
 */
export function render(state, ui = {}) {
  syncSimpleInputs(state);
  syncItemsTable(state);
  syncTotals(state);
  syncSavedIndicator(ui.savedAt ?? null);
}

/**
 * Bind vstupu na update stavu. Vola se jednou pri startu.
 * @param {{
 *   onField: (path: string, value: string) => void,
 *   onItemField: (id: string, field: string, value: string) => void,
 *   onItemAdd: () => void,
 *   onItemRemove: (id: string) => void,
 *   onDate: (key: string, iso: string) => void,
 *   onNumber: (value: string) => void,
 *   onPrint: () => void,
 *   onReset: () => void,
 *   onExport: () => void,
 *   onImport: (file: File) => void,
 * }} handlers
 */
export function bindEvents(handlers) {
  // Staticka pole (dodavatel, odberatel, payment, meta)
  document.querySelectorAll('[data-field]').forEach((el) => {
    el.addEventListener('input', () => {
      handlers.onField(el.dataset.field, getElValue(el));
    });
  });

  // Cislo faktury
  const numberEl = document.getElementById('invoice-number');
  if (numberEl) {
    numberEl.addEventListener('input', () => handlers.onNumber(getElValue(numberEl)));
  }

  // Datumy
  document.querySelectorAll('[data-date]').forEach((el) => {
    el.addEventListener('change', () => handlers.onDate(el.dataset.date, getElValue(el)));
  });

  // Tlacitka v tabulce polozek (delegace na tbody, kvuli rebuildu radku)
  const itemsBody = document.querySelector('#polozky tbody');
  if (itemsBody) {
    itemsBody.addEventListener('input', (ev) => {
      const target = ev.target;
      const tr = target.closest('tr[data-item-id]');
      if (!tr) return;
      const field = target.dataset.itemField;
      if (!field) return;
      handlers.onItemField(tr.dataset.itemId, field, getElValue(target));
    });
    itemsBody.addEventListener('change', (ev) => {
      // change event pro <select> – stejna logika jako input
      const target = ev.target;
      if (!(target instanceof HTMLSelectElement)) return;
      const tr = target.closest('tr[data-item-id]');
      if (!tr) return;
      const field = target.dataset.itemField;
      if (!field) return;
      handlers.onItemField(tr.dataset.itemId, field, getElValue(target));
    });
    itemsBody.addEventListener('click', (ev) => {
      const btn = ev.target.closest('button.del-row');
      if (!btn) return;
      const tr = btn.closest('tr[data-item-id]');
      if (!tr) return;
      handlers.onItemRemove(tr.dataset.itemId);
    });
  }

  document.getElementById('add-row')?.addEventListener('click', handlers.onItemAdd);
  document.getElementById('btn-print')?.addEventListener('click', handlers.onPrint);
  document.getElementById('btn-reset')?.addEventListener('click', handlers.onReset);
  document.getElementById('btn-export')?.addEventListener('click', handlers.onExport);

  const importInput = document.getElementById('btn-import');
  if (importInput) {
    importInput.addEventListener('change', () => {
      const file = importInput.files?.[0];
      if (file) {
        handlers.onImport(file);
        importInput.value = '';
      }
    });
  }
}

function syncSimpleInputs(state) {
  setValue(document.getElementById('invoice-number'), state.number);

  document.querySelectorAll('[data-field]').forEach((el) => {
    const value = readPath(state, el.dataset.field);
    setValue(el, value ?? '');
  });

  document.querySelectorAll('[data-display]').forEach((el) => {
    const value = readPath(state, el.dataset.display);
    el.textContent = value == null ? '' : String(value);
  });

  document.querySelectorAll('[data-date]').forEach((el) => {
    const key = el.dataset.date;
    setValue(el, state.dates?.[key] ?? '');
  });
}

function syncItemsTable(state) {
  const tbody = document.querySelector('#polozky tbody');
  if (!tbody) return;

  const ids = state.items.map((it) => it.id).join('|');
  if (ids !== cachedItemIds) {
    rebuildItemRows(tbody, state.items);
    cachedItemIds = ids;
  } else {
    syncItemRowValues(tbody, state.items);
  }

  // Ne-prazdny vypocet ceny bez DPH per radek
  state.items.forEach((item) => {
    const row = tbody.querySelector(`tr[data-item-id="${cssEscape(item.id)}"]`);
    if (!row) return;
    const t = lineTotals(item);
    const cell = row.querySelector('.polozka-cena');
    if (cell) cell.textContent = formatMoney(t.base, state.meta.currency);
  });

  // Tlacitko smazani – povolene jen kdyz mam vic nez 1 radek
  const delButtons = tbody.querySelectorAll('button.del-row');
  delButtons.forEach((btn) => {
    btn.disabled = state.items.length <= 1;
  });
}

function rebuildItemRows(tbody, items) {
  tbody.replaceChildren(...items.map((item) => buildItemRow(item)));
}

function buildItemRow(item) {
  const tr = document.createElement('tr');
  tr.className = 'polozka';
  tr.dataset.itemId = item.id;

  tr.appendChild(td('', (cell) => {
    const input = document.createElement('input');
    input.type = 'text';
    input.dataset.itemField = 'name';
    input.value = item.name ?? '';
    input.placeholder = 'Název produktu / služby';
    cell.appendChild(input);
  }));

  tr.appendChild(td('c polozka-pocet', (cell) => {
    const input = document.createElement('input');
    input.type = 'text';
    input.inputMode = 'decimal';
    input.dataset.itemField = 'qty';
    input.className = 'r';
    input.value = formatNumberInput(item.qty);
    cell.appendChild(input);
  }));

  tr.appendChild(td('c', (cell) => {
    const sel = document.createElement('select');
    sel.dataset.itemField = 'unit';
    UNITS.forEach((u) => {
      const opt = document.createElement('option');
      opt.value = u;
      opt.textContent = u;
      if (u === item.unit) opt.selected = true;
      sel.appendChild(opt);
    });
    cell.appendChild(sel);
  }));

  tr.appendChild(td('r polozka-cena-jednotka nowrap', (cell) => {
    const input = document.createElement('input');
    input.type = 'text';
    input.inputMode = 'decimal';
    input.dataset.itemField = 'price';
    input.className = 'r';
    input.value = formatNumberInput(item.price);
    cell.appendChild(input);
    cell.appendChild(document.createTextNode(' Kč'));
  }));

  tr.appendChild(td('c nowrap', (cell) => {
    const sel = document.createElement('select');
    sel.dataset.itemField = 'vat';
    VAT_RATES.forEach((rate) => {
      const opt = document.createElement('option');
      opt.value = String(rate);
      opt.textContent = String(rate);
      if (Number(rate) === Number(item.vat)) opt.selected = true;
      sel.appendChild(opt);
    });
    cell.appendChild(sel);
    cell.appendChild(document.createTextNode(' %'));
  }));

  const cenaCell = td('r polozka-cena nowrap', () => {});
  tr.appendChild(cenaCell);

  tr.appendChild(td('', (cell) => {
    const input = document.createElement('input');
    input.type = 'text';
    input.dataset.itemField = 'note';
    input.value = item.note ?? '';
    cell.appendChild(input);
  }));

  tr.appendChild(td('hide-p b-0 p-0', (cell) => {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'del-row hide-p';
    btn.textContent = '−';
    btn.title = 'Smazat řádek';
    cell.appendChild(btn);
  }));

  return tr;
}

function syncItemRowValues(tbody, items) {
  items.forEach((item) => {
    const row = tbody.querySelector(`tr[data-item-id="${cssEscape(item.id)}"]`);
    if (!row) return;
    setItemFieldValue(row, 'name', item.name ?? '');
    setItemFieldValue(row, 'qty', formatNumberInput(item.qty));
    setItemFieldValue(row, 'unit', item.unit ?? '');
    setItemFieldValue(row, 'price', formatNumberInput(item.price));
    setItemFieldValue(row, 'vat', String(item.vat));
    setItemFieldValue(row, 'note', item.note ?? '');
  });
}

function setItemFieldValue(row, field, value) {
  const el = row.querySelector(`[data-item-field="${field}"]`);
  if (!el) return;
  setValue(el, value);
}

function syncTotals(state) {
  const totals = invoiceTotals(state.items);
  const currency = state.meta.currency;

  const sumaceBody = document.querySelector('#sumace tbody');
  if (sumaceBody) {
    if (totals.groups.length === 0) {
      sumaceBody.replaceChildren(buildSumaceRow({ rate: 0, base: 0, vat: 0, total: 0 }, currency));
    } else {
      sumaceBody.replaceChildren(...totals.groups.map((g) => buildSumaceRow(g, currency)));
    }
  }

  setText('suma-zaklad', formatMoney(totals.base, currency));
  setText('suma-dph', formatMoney(totals.vat, currency));
  setText('suma-celkem', formatMoney(totals.total, currency));
  setText('suma-celkem-1', formatMoney(totals.total, currency));
}

function buildSumaceRow(group, currency) {
  const tr = document.createElement('tr');
  tr.appendChild(td('c', (cell) => { cell.textContent = `${group.rate} %`; }));
  tr.appendChild(td('r', (cell) => { cell.textContent = formatMoney(group.base, currency); }));
  tr.appendChild(td('r', (cell) => { cell.textContent = formatMoney(group.vat, currency); }));
  tr.appendChild(td('r', (cell) => { cell.textContent = formatMoney(group.total, currency); }));
  return tr;
}

function syncSavedIndicator(savedAt) {
  const el = document.getElementById('saved-indicator');
  if (!el) return;
  if (!savedAt) {
    el.textContent = '';
    return;
  }
  const d = new Date(savedAt);
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  el.textContent = `Uloženo ${hh}:${mm}`;
}

function td(className, build) {
  const cell = document.createElement('td');
  if (className) cell.className = className;
  build(cell);
  return cell;
}

function setText(id, value) {
  const el = document.getElementById(id);
  if (el) el.textContent = value;
}

function setValue(el, value) {
  if (!el) return;
  const next = value == null ? '' : String(value);
  // Aktivni input nepretahujeme – jinak by se cursor sam stale resetoval.
  if (el === document.activeElement) return;
  if (el.value !== next) el.value = next;
}

function getElValue(el) {
  return el.value;
}

function readPath(state, path) {
  if (!path) return undefined;
  return path.split('.').reduce((acc, key) => (acc == null ? acc : acc[key]), state);
}

function formatNumberInput(value) {
  if (value === '' || value == null) return '';
  if (Number.isFinite(Number(value))) return String(value);
  return '';
}

function cssEscape(s) {
  if (typeof CSS !== 'undefined' && typeof CSS.escape === 'function') return CSS.escape(s);
  return String(s).replace(/"/g, '\\"');
}

export { parseNumber };
